import type { FeedbackCopy, SupportedMarketingLocale } from "./types";

export const feedback: Record<SupportedMarketingLocale, FeedbackCopy> = {
  en: {
    seo: {
      title: "Anonymous coach feedback for grassroots football — Vote.GameOn",
      description:
        "A 5-dimension radar (Smiles, Fair Go, Skill Boost, Big Cheers, Team Spark) collected anonymously after every match. Free for coaches. Built into the player vote.",
    },
    srHeading: "Anonymous coach feedback that grassroots parents will actually fill in",
    hero: {
      eyebrow: "Coach feedback that's actually anonymous",
      headline: "Find out how you're really doing.",
      headlineHighlight: "From the people who'd never say it to your face.",
      subhead:
        "After every match, voters can rate you across five dimensions in ten seconds. You see a radar chart, a trend line, and the occasional comment that actually changes how you coach.",
      cta: "Start a free team",
      seeHow: "See how it works",
      mockEyebrow: "After the match",
      mockTitle: "Rate the coach",
      mockFooter: "Anonymous. 10 seconds. Optional.",
      mockBadge: "23 / 25 · Big Cheers",
    },
    categories: {
      smiles: { label: "Smiles", description: "How positive and encouraging was the coaching?" },
      fairGo: { label: "Fair Go", description: "How fair was the playing time and opportunity distribution?" },
      skillBoost: { label: "Skill Boost", description: "How well did the coaching help improve skills?" },
      bigCheers: { label: "Big Cheers", description: "How motivational and supportive was the coaching?" },
      teamSpark: { label: "Team Spark", description: "How well did the coaching build team spirit?" },
    },
    problem: {
      eyebrow: "The blind spot every grassroots coach has",
      cards: [
        { title: "Parents won't tell you", body: "Most people would rather grumble in the WhatsApp side-chat than raise it with the coach directly." },
        { title: "End-of-season is too late", body: "By the time the club survey lands in November, the moments worth fixing are months gone." },
        { title: "Vague praise doesn't help", body: "\"Good game coach\" tells you nothing. You need to know what worked and what didn't." },
      ],
    },
    howItWorks: {
      heading: "Three steps. Built into the vote your team already uses.",
      subhead: "No second link to share, no separate survey to chase. Just an extra screen at the end of the player-of-the-match vote.",
      steps: [
        { badge: "Existing flow", title: "Voter casts a player vote", body: "Same anonymous link they always use. No new flow to learn, no extra tap to install anything." },
        { badge: "10 seconds", title: "Optionally rates the coach", body: "Five sliders, plain-language labels. Skip if you want — most don't, because it's that quick." },
        { badge: "Private", title: "Coach sees the radar + trend", body: "Aggregate radar across all responses, plus a season trend line. Comments stay anonymous, attached to the game." },
      ],
    },
    radar: {
      heading: "Five dimensions. One picture.",
      subhead: "Not a single \"how was the coach today?\" star rating — five specific things that actually matter, scored out of five.",
      chartCaption: "Average rating",
      sampleSize: "Sample radar across 18 anonymous responses",
      footnote: "Voters see the same five categories every game — so a \"4 on Skill Boost\" means the same thing in March as it does in October.",
    },
    features: {
      heading: "Built for coaches who actually want to get better",
      subhead: "Honest feedback, structured properly, with the privacy guarantees that make people answer honestly.",
      cards: [
        { title: "Genuinely anonymous", body: "No accounts, no IP fingerprinting on responses, no way to map a comment back to a parent. People only tell the truth when they're sure of that." },
        { title: "Opt-in per team", body: "Off by default. Coaches choose to invite feedback when they're ready — and can pause it any time from team settings." },
        { title: "Multi-dimensional radar", body: "Five categories — Smiles, Fair Go, Skill Boost, Big Cheers, Team Spark — so feedback is specific, not just a vague star rating." },
        { title: "Season trend chart", body: "Watch each dimension move over the year. Spot the slump in March before it becomes the resignation in May." },
        { title: "Free-text comments", body: "Optional written notes attached to each response. Where the real gold is — the things people would never say out loud." },
        { title: "Pairs with peer voting", body: "Top-down feedback (parents → coach) and bottom-up recognition (players → players) on the same platform. Two perspectives, one source of truth." },
      ],
    },
    science: {
      eyebrow: "Backed by sports psychology",
      heading: "The research behind the five dimensions.",
      body: "In a 2025 intervention study, Berntzen and Lagestad had 95 youth footballers aged 14 to 18 train the same session twice with the same coach, once with feedback and once without. Feedback raised the players' sense of mastery, of being seen by the coach, and of motivation. (Frontiers in Sports and Active Living, DOI 10.3389/fspor.2025.1527543.)",
      link: "Read the research behind it",
    },
    comparison: {
      heading: "Why not just ask in the WhatsApp group?",
      subhead: "Because nobody answers honestly when their name is on the message.",
      columnFeature: "Capability",
      columnUs: "Vote.GameOn",
      columnThem: "Group chat / surveys",
      rows: [
        "Truly anonymous responses",
        "Captures five distinct dimensions, not one star",
        "Available after every game, not once a season",
        "Lives where parents already are (the vote link)",
        "Trend chart shows progress over time",
        "Free for coaches to use",
      ],
      footnote: "Comparison reflects typical grassroots alternatives (group chat polls and once-a-season club surveys) as of 2025.",
    },
    finalCta: {
      heading: "Start collecting feedback ",
      headlineHighlight: "this week.",
      subhead: "Free for coaches. Off by default — switch it on when you're ready to hear what people really think.",
      cta: "Create your team — free",
      footnote: "Anonymous. Structured. Actually useful.",
    },
  },
  es: {
    seo: {
      title: "Evaluación anónima al entrenador para fútbol amateur — Vote.GameOn",
      description:
        "Un radar de 5 dimensiones (Sonrisas, Juego Justo, Mejora Técnica, Aliento, Espíritu de Equipo) que se recoge de forma anónima después de cada partido. Gratis para entrenadores. Integrado a la votación.",
    },
    srHeading: "Evaluación anónima al entrenador que los padres del fútbol amateur sí completan",
    hero: {
      eyebrow: "Evaluación al entrenador, de verdad anónima",
      headline: "Descubre cómo te está yendo de verdad.",
      headlineHighlight: "De parte de la gente que jamás te lo diría a la cara.",
      subhead:
        "Después de cada partido, quienes votan pueden evaluarte en cinco dimensiones en diez segundos. Tú ves un gráfico de radar, una tendencia de temporada y, de vez en cuando, un comentario que sí cambia cómo entrenas.",
      cta: "Crear un equipo gratis",
      seeHow: "Cómo funciona",
      mockEyebrow: "Después del partido",
      mockTitle: "Evalúa al entrenador",
      mockFooter: "Anónimo. 10 segundos. Opcional.",
      mockBadge: "23 / 25 · Aliento",
    },
    categories: {
      smiles: { label: "Sonrisas", description: "¿Qué tan positivo y alentador fue el entrenador?" },
      fairGo: { label: "Juego justo", description: "¿Qué tan justo fue el reparto de minutos y oportunidades?" },
      skillBoost: { label: "Mejora técnica", description: "¿Cuánto ayudó el entrenador a mejorar las habilidades?" },
      bigCheers: { label: "Aliento", description: "¿Qué tan motivador y de apoyo fue el entrenador?" },
      teamSpark: { label: "Espíritu de equipo", description: "¿Qué tan bien construyó el espíritu de equipo?" },
    },
    problem: {
      eyebrow: "El punto ciego que tiene todo entrenador amateur",
      cards: [
        { title: "Los padres no te lo dirán", body: "La mayoría prefiere quejarse en el chat paralelo de WhatsApp antes que decírtelo a ti directamente." },
        { title: "Fin de temporada es muy tarde", body: "Cuando llega la encuesta del club en noviembre, los momentos que valía la pena corregir ya pasaron hace meses." },
        { title: "Los elogios vagos no ayudan", body: "\"Buen partido, profe\" no te dice nada. Necesitas saber qué funcionó y qué no." },
      ],
    },
    howItWorks: {
      heading: "Tres pasos. Integrados a la votación que tu equipo ya usa.",
      subhead: "Ningún segundo enlace que compartir, ninguna encuesta extra que perseguir. Solo una pantalla más al final de la votación al jugador del partido.",
      steps: [
        { badge: "Flujo existente", title: "Quien vota elige al jugador", body: "El mismo enlace anónimo de siempre. Sin flujo nuevo que aprender, sin nada que instalar." },
        { badge: "10 segundos", title: "Si quiere, evalúa al entrenador", body: "Cinco sliders, etiquetas claras. Se puede saltar — casi nadie lo hace, porque es así de rápido." },
        { badge: "Privado", title: "El entrenador ve el radar + tendencia", body: "Radar agregado de todas las respuestas y una tendencia de temporada. Los comentarios quedan anónimos, ligados al partido." },
      ],
    },
    radar: {
      heading: "Cinco dimensiones. Una sola imagen.",
      subhead:
        "No una sola estrella de \"¿qué tal el profe hoy?\" — cinco cosas concretas que sí importan, evaluadas sobre cinco.",
      chartCaption: "Promedio",
      sampleSize: "Radar de ejemplo con 18 respuestas anónimas",
      footnote:
        "Los votantes ven las mismas cinco categorías cada partido — así un \"4 en Mejora técnica\" significa lo mismo en marzo que en octubre.",
    },
    features: {
      heading: "Hecho para los entrenadores que de verdad quieren mejorar",
      subhead: "Feedback honesto, bien estructurado, con las garantías de privacidad que hacen que la gente sí responda con la verdad.",
      cards: [
        { title: "De verdad anónimo", body: "Sin cuentas, sin huella de IP en las respuestas, sin manera de relacionar un comentario con un padre. La gente solo dice la verdad cuando está segura de eso." },
        { title: "Opt-in por equipo", body: "Apagado por defecto. El entrenador decide cuándo invitar feedback — y puede pausarlo en cualquier momento desde la configuración del equipo." },
        { title: "Radar multidimensional", body: "Cinco categorías — Sonrisas, Juego Justo, Mejora Técnica, Aliento, Espíritu de Equipo — para que el feedback sea específico, no una estrella vaga." },
        { title: "Tendencia de temporada", body: "Mira cómo se mueve cada dimensión en el año. Detecta el bajón de marzo antes de que sea la renuncia de mayo." },
        { title: "Comentarios libres", body: "Notas opcionales en cada respuesta. Ahí está el verdadero oro — las cosas que la gente nunca diría en voz alta." },
        { title: "Se complementa con la votación entre pares", body: "Feedback de arriba hacia abajo (padres → entrenador) y reconocimiento de abajo hacia arriba (jugadores → jugadores) en la misma plataforma. Dos perspectivas, una sola fuente de verdad." },
      ],
    },
    science: {
      eyebrow: "Respaldado por la psicología del deporte",
      heading: "La investigación detrás de las cinco dimensiones.",
      body: "En un estudio de intervención de 2025, Berntzen y Lagestad hicieron que 95 futbolistas juveniles de 14 a 18 años entrenaran la misma sesión dos veces con el mismo entrenador, una con feedback y otra sin él. El feedback aumentó la sensación de dominio de los jugadores, la de sentirse vistos por el entrenador y la motivación. (Frontiers in Sports and Active Living, DOI 10.3389/fspor.2025.1527543.)",
      link: "Lee la investigación que lo respalda",
    },
    comparison: {
      heading: "¿Por qué no preguntar simplemente en el grupo de WhatsApp?",
      subhead: "Porque nadie responde con honestidad cuando su nombre va en el mensaje.",
      columnFeature: "Capacidad",
      columnUs: "Vote.GameOn",
      columnThem: "Chat grupal / encuestas",
      rows: [
        "Respuestas de verdad anónimas",
        "Captura cinco dimensiones distintas, no una estrella",
        "Disponible después de cada partido, no una vez por temporada",
        "Vive donde los padres ya están (el enlace de votación)",
        "Tendencia que muestra el progreso en el tiempo",
        "Gratis para los entrenadores",
      ],
      footnote: "La comparación refleja las alternativas amateurs típicas (encuestas de chat grupal y encuestas de club una vez por temporada) a 2025.",
    },
    finalCta: {
      heading: "Empieza a recibir feedback ",
      headlineHighlight: "esta semana.",
      subhead: "Gratis para entrenadores. Apagado por defecto — actívalo cuando estés listo para escuchar lo que la gente realmente piensa.",
      cta: "Crea tu equipo — gratis",
      footnote: "Anónimo. Estructurado. De verdad útil.",
    },
  },
  "pt-BR": {
    seo: {
      title: "Avaliação anônima do treinador para o futebol de base — Vote.GameOn",
      description:
        "Um radar de 5 dimensões (Sorrisos, Jogo Justo, Evolução Técnica, Incentivo, Espírito de Equipe) coletado de forma anônima após cada jogo. Grátis para treinadores. Integrado à votação.",
    },
    srHeading: "Avaliação anônima do treinador que os pais do futebol de base realmente preenchem",
    hero: {
      eyebrow: "Avaliação do treinador, de verdade anônima",
      headline: "Descubra como você está indo de verdade.",
      headlineHighlight: "Da parte de quem nunca diria na sua cara.",
      subhead:
        "Após cada jogo, quem vota pode te avaliar em cinco dimensões em dez segundos. Você vê um radar, uma tendência de temporada e, vez ou outra, um comentário que muda mesmo o seu jeito de treinar.",
      cta: "Criar um time grátis",
      seeHow: "Como funciona",
      mockEyebrow: "Após o jogo",
      mockTitle: "Avalie o treinador",
      mockFooter: "Anônimo. 10 segundos. Opcional.",
      mockBadge: "23 / 25 · Incentivo",
    },
    categories: {
      smiles: { label: "Sorrisos", description: "O quanto o treinador foi positivo e encorajador?" },
      fairGo: { label: "Jogo justo", description: "O quanto foi justa a divisão de tempo de jogo e oportunidades?" },
      skillBoost: { label: "Evolução técnica", description: "O quanto o treino ajudou a melhorar habilidades?" },
      bigCheers: { label: "Incentivo", description: "O quanto o treinador foi motivador e dava apoio?" },
      teamSpark: { label: "Espírito de equipe", description: "O quanto o treinador construiu o espírito de equipe?" },
    },
    problem: {
      eyebrow: "O ponto cego que todo treinador de base tem",
      cards: [
        { title: "Os pais não vão te dizer", body: "A maioria prefere reclamar no chat paralelo do WhatsApp a falar diretamente com o treinador." },
        { title: "Fim de temporada é tarde demais", body: "Quando a pesquisa do clube chega em novembro, os momentos que valeria corrigir já passaram há meses." },
        { title: "Elogios vagos não ajudam", body: "\"Bom jogo, professor\" não te diz nada. Você precisa saber o que funcionou e o que não." },
      ],
    },
    howItWorks: {
      heading: "Três passos. Integrados à votação que seu time já usa.",
      subhead: "Sem segundo link para compartilhar, sem pesquisa extra para cobrar. Só uma tela a mais ao final do voto no craque do jogo.",
      steps: [
        { badge: "Fluxo existente", title: "Quem vota escolhe o jogador", body: "O mesmo link anônimo de sempre. Sem fluxo novo para aprender, nada novo para instalar." },
        { badge: "10 segundos", title: "Se quiser, avalia o treinador", body: "Cinco sliders, rótulos claros. Pode pular — quase ninguém pula, de tão rápido que é." },
        { badge: "Privado", title: "O treinador vê o radar + tendência", body: "Radar agregado de todas as respostas e tendência de temporada. Os comentários ficam anônimos, ligados ao jogo." },
      ],
    },
    radar: {
      heading: "Cinco dimensões. Uma única imagem.",
      subhead: "Não uma estrelinha de \"como foi o treinador hoje?\" — cinco coisas concretas que importam, avaliadas de 1 a 5.",
      chartCaption: "Média",
      sampleSize: "Radar de exemplo com 18 respostas anônimas",
      footnote: "Quem vota vê as mesmas cinco categorias todo jogo — assim um \"4 em Evolução técnica\" significa o mesmo em março e em outubro.",
    },
    features: {
      heading: "Feito para os treinadores que querem mesmo melhorar",
      subhead: "Feedback honesto, bem estruturado, com as garantias de privacidade que fazem as pessoas responderem com sinceridade.",
      cards: [
        { title: "De verdade anônimo", body: "Sem contas, sem impressão digital de IP nas respostas, sem como ligar um comentário a um pai. As pessoas só dizem a verdade quando têm certeza disso." },
        { title: "Opt-in por equipe", body: "Desligado por padrão. O treinador escolhe quando convidar feedback — e pode pausar a qualquer momento nas configurações do time." },
        { title: "Radar multidimensional", body: "Cinco categorias — Sorrisos, Jogo Justo, Evolução Técnica, Incentivo, Espírito de Equipe — para um feedback específico, não uma estrela vaga." },
        { title: "Tendência de temporada", body: "Veja como cada dimensão se mexe ao longo do ano. Detecte a queda de março antes que vire a renúncia em maio." },
        { title: "Comentários livres", body: "Notas opcionais em cada resposta. É aí que está o ouro — o que ninguém diria em voz alta." },
        { title: "Combina com a votação entre pares", body: "Feedback de cima para baixo (pais → treinador) e reconhecimento de baixo para cima (jogadores → jogadores) na mesma plataforma. Duas perspectivas, uma única fonte de verdade." },
      ],
    },
    science: {
      eyebrow: "Respaldado pela psicologia do esporte",
      heading: "A pesquisa por trás das cinco dimensões.",
      body: "Em um estudo de intervenção de 2025, Berntzen e Lagestad fizeram 95 jogadores juvenis de 14 a 18 anos treinarem a mesma sessão duas vezes com o mesmo treinador, uma com feedback e outra sem. O feedback aumentou a sensação de domínio dos jogadores, a de serem vistos pelo treinador e a motivação. (Frontiers in Sports and Active Living, DOI 10.3389/fspor.2025.1527543.)",
      link: "Leia a pesquisa por trás",
    },
    comparison: {
      heading: "Por que não perguntar simplesmente no grupo do WhatsApp?",
      subhead: "Porque ninguém responde com honestidade quando o nome dele aparece na mensagem.",
      columnFeature: "Capacidade",
      columnUs: "Vote.GameOn",
      columnThem: "Grupo / pesquisas",
      rows: [
        "Respostas de verdade anônimas",
        "Captura cinco dimensões distintas, não uma estrela",
        "Disponível após cada jogo, não uma vez por temporada",
        "Vive onde os pais já estão (o link de votação)",
        "Tendência que mostra o progresso no tempo",
        "Grátis para os treinadores",
      ],
      footnote: "A comparação reflete as alternativas típicas do futebol de base (enquetes em grupo e pesquisas anuais do clube) em 2025.",
    },
    finalCta: {
      heading: "Comece a coletar feedback ",
      headlineHighlight: "esta semana.",
      subhead: "Grátis para treinadores. Desligado por padrão — ative quando estiver pronto para ouvir o que as pessoas realmente pensam.",
      cta: "Crie seu time — grátis",
      footnote: "Anônimo. Estruturado. Realmente útil.",
    },
  },
  fr: {
    seo: {
      title: "Évaluation anonyme de l'entraîneur pour le foot amateur — Vote.GameOn",
      description:
        "Un radar à 5 dimensions (Sourires, Fair-play, Progrès technique, Encouragements, Esprit d'équipe) collecté anonymement après chaque match. Gratuit pour les entraîneurs. Intégré au vote.",
    },
    srHeading: "Une évaluation anonyme de l'entraîneur que les parents du foot amateur remplissent vraiment",
    hero: {
      eyebrow: "Une évaluation entraîneur vraiment anonyme",
      headline: "Découvrez où vous en êtes vraiment.",
      headlineHighlight: "De la part de gens qui ne vous le diraient jamais en face.",
      subhead:
        "Après chaque match, les votants peuvent vous évaluer sur cinq dimensions en dix secondes. Vous voyez un radar, une tendance de saison et, parfois, un commentaire qui change vraiment votre façon d'entraîner.",
      cta: "Créer une équipe gratuite",
      seeHow: "Voir comment ça marche",
      mockEyebrow: "Après le match",
      mockTitle: "Évaluer l'entraîneur",
      mockFooter: "Anonyme. 10 secondes. Facultatif.",
      mockBadge: "23 / 25 · Encouragements",
    },
    categories: {
      smiles: { label: "Sourires", description: "À quel point l'entraînement était positif et encourageant ?" },
      fairGo: { label: "Fair-play", description: "Le temps de jeu et les opportunités étaient-ils répartis équitablement ?" },
      skillBoost: { label: "Progrès technique", description: "L'entraînement a-t-il aidé à progresser techniquement ?" },
      bigCheers: { label: "Encouragements", description: "L'entraîneur était-il motivant et soutenant ?" },
      teamSpark: { label: "Esprit d'équipe", description: "L'entraîneur a-t-il bien construit l'esprit d'équipe ?" },
    },
    problem: {
      eyebrow: "L'angle mort de tout entraîneur amateur",
      cards: [
        { title: "Les parents ne vous diront pas", body: "La plupart préfèrent râler dans le chat parallèle WhatsApp plutôt que de vous le dire en face." },
        { title: "La fin de saison, c'est trop tard", body: "Quand l'enquête du club arrive en novembre, les moments à corriger sont passés depuis des mois." },
        { title: "Les compliments vagues n'aident pas", body: "\"Bon match coach\" ne vous dit rien. Il faut savoir ce qui a marché et ce qui n'a pas marché." },
      ],
    },
    howItWorks: {
      heading: "Trois étapes. Intégrées au vote que votre équipe utilise déjà.",
      subhead: "Pas de second lien à partager, pas d'enquête à courser. Juste un écran de plus à la fin du vote du joueur du match.",
      steps: [
        { badge: "Flux existant", title: "Le votant choisit un joueur", body: "Le même lien anonyme que d'habitude. Pas de nouveau flux à apprendre, rien à installer." },
        { badge: "10 secondes", title: "Évalue l'entraîneur s'il le souhaite", body: "Cinq sliders, libellés clairs. On peut passer — la plupart ne le font pas, tellement c'est rapide." },
        { badge: "Privé", title: "L'entraîneur voit le radar + la tendance", body: "Radar agrégé de toutes les réponses et tendance de saison. Les commentaires restent anonymes, rattachés au match." },
      ],
    },
    radar: {
      heading: "Cinq dimensions. Une seule image.",
      subhead: "Pas une étoile unique \"comment était le coach ?\" — cinq choses précises qui comptent, notées sur cinq.",
      chartCaption: "Note moyenne",
      sampleSize: "Radar exemple sur 18 réponses anonymes",
      footnote: "Les votants voient les mêmes cinq catégories à chaque match — un \"4 en Progrès technique\" veut dire la même chose en mars et en octobre.",
    },
    features: {
      heading: "Pour les entraîneurs qui veulent vraiment progresser",
      subhead: "Un feedback honnête, bien structuré, avec les garanties de confidentialité qui font qu'on répond vraiment.",
      cards: [
        { title: "Vraiment anonyme", body: "Pas de comptes, pas d'empreinte IP sur les réponses, aucun moyen de relier un commentaire à un parent. Les gens ne disent la vérité que quand ils en sont certains." },
        { title: "Activable par équipe", body: "Désactivé par défaut. L'entraîneur choisit quand inviter du feedback — et peut le mettre en pause à tout moment depuis les paramètres." },
        { title: "Radar multidimensionnel", body: "Cinq catégories — Sourires, Fair-play, Progrès technique, Encouragements, Esprit d'équipe — pour un feedback précis, pas une étoile vague." },
        { title: "Tendance de saison", body: "Suivez chaque dimension dans l'année. Repérez la baisse de mars avant qu'elle ne devienne la démission de mai." },
        { title: "Commentaires libres", body: "Notes facultatives sur chaque réponse. C'est là qu'est l'or — les choses que personne ne dirait à voix haute." },
        { title: "Complète le vote entre pairs", body: "Feedback descendant (parents → entraîneur) et reconnaissance ascendante (joueurs → joueurs) sur la même plateforme. Deux perspectives, une seule source de vérité." },
      ],
    },
    science: {
      eyebrow: "Adossé à la psychologie du sport",
      heading: "La recherche derrière les cinq dimensions.",
      body: "Dans une étude d'intervention de 2025, Berntzen et Lagestad ont fait entraîner 95 jeunes footballeurs de 14 à 18 ans sur la même séance deux fois avec le même entraîneur, une fois avec feedback et une fois sans. Le feedback a augmenté leur sentiment de maîtrise, celui d'être vus par l'entraîneur et leur motivation. (Frontiers in Sports and Active Living, DOI 10.3389/fspor.2025.1527543.)",
      link: "Lire la recherche derrière",
    },
    comparison: {
      heading: "Pourquoi pas simplement demander dans le groupe WhatsApp ?",
      subhead: "Parce que personne ne répond honnêtement quand son nom apparaît dans le message.",
      columnFeature: "Capacité",
      columnUs: "Vote.GameOn",
      columnThem: "Groupe / sondages",
      rows: [
        "Réponses vraiment anonymes",
        "Capture cinq dimensions distinctes, pas une seule étoile",
        "Disponible après chaque match, pas une fois par saison",
        "Vit là où les parents sont déjà (le lien de vote)",
        "Tendance qui montre la progression dans le temps",
        "Gratuit pour les entraîneurs",
      ],
      footnote: "La comparaison reflète les alternatives amateur typiques (sondages WhatsApp et enquêtes annuelles de club) en 2025.",
    },
    finalCta: {
      heading: "Commencez à recueillir du feedback ",
      headlineHighlight: "cette semaine.",
      subhead: "Gratuit pour les entraîneurs. Désactivé par défaut — activez-le quand vous êtes prêt à entendre ce que les gens pensent vraiment.",
      cta: "Créez votre équipe — gratuit",
      footnote: "Anonyme. Structuré. Vraiment utile.",
    },
  },
  ja: {
    seo: {
      title: "草の根フットボールの匿名コーチ評価 — Vote.GameOn",
      description:
        "毎試合後に匿名で集める5次元レーダー（笑顔・公平さ・スキル向上・応援・チーム熱量）。コーチは無料、選手投票に組み込み済み。",
    },
    srHeading: "草の根の保護者が本当に答えてくれる、匿名のコーチ評価",
    hero: {
      eyebrow: "本当に匿名のコーチ評価",
      headline: "自分が実際どう見られているか、知ろう。",
      headlineHighlight: "面と向かっては絶対言わない人たちから。",
      subhead:
        "毎試合後、投票者は5つの次元で10秒で評価できます。レーダーチャート、シーズンの推移、そして時に指導を本当に変えるコメントが届きます。",
      cta: "無料でチームを作る",
      seeHow: "仕組みを見る",
      mockEyebrow: "試合後",
      mockTitle: "コーチを評価",
      mockFooter: "匿名。10秒。任意。",
      mockBadge: "23 / 25 ・応援",
    },
    categories: {
      smiles: { label: "笑顔", description: "指導はどれだけ前向きで励みになったか？" },
      fairGo: { label: "公平さ", description: "出場時間や機会の配分はどれだけ公平だったか？" },
      skillBoost: { label: "スキル向上", description: "指導はどれだけスキルの向上に役立ったか？" },
      bigCheers: { label: "応援", description: "指導はどれだけモチベーティブで支えとなったか？" },
      teamSpark: { label: "チーム熱量", description: "指導はどれだけチームの一体感を育てたか？" },
    },
    problem: {
      eyebrow: "草の根コーチが必ず持つ盲点",
      cards: [
        { title: "保護者は本音を言わない", body: "多くの人はコーチに直接伝えるより、WhatsAppの裏グループで愚痴る方を選びます。" },
        { title: "シーズン末では遅すぎる", body: "クラブのアンケートが11月に届く頃には、直すべき瞬間はもう何ヶ月も前。" },
        { title: "ぼんやりした褒め言葉は役に立たない", body: "「いい試合でしたね」では何も分かりません。何が効いて、何が効かなかったかが必要です。" },
      ],
    },
    howItWorks: {
      heading: "3ステップ。チームがすでに使っている投票に組み込まれています。",
      subhead: "2つ目のリンクを配る必要も、別アンケートを追いかける必要もなし。MVP投票の最後に画面が1つ追加されるだけ。",
      steps: [
        { badge: "既存の流れ", title: "投票者が選手に投票", body: "いつもと同じ匿名リンク。新しい流れを覚える必要も、追加インストールも不要。" },
        { badge: "10秒", title: "任意でコーチを評価", body: "スライダー5本、日本語ラベル。スキップも可能ですが、あまりの速さにほぼ全員が回答します。" },
        { badge: "非公開", title: "コーチはレーダー＋推移を確認", body: "全回答の集計レーダーとシーズン推移。コメントは試合に紐づく匿名のまま保持されます。" },
      ],
    },
    radar: {
      heading: "5次元、1枚の絵で。",
      subhead: "「今日のコーチはどうだった？」の星ひとつではなく、本当に大事な5つを5点満点で。",
      chartCaption: "平均評価",
      sampleSize: "18件の匿名回答によるサンプルレーダー",
      footnote: "投票者は毎試合同じ5カテゴリーを見ます — 3月の「スキル向上4」と10月の「スキル向上4」は同じ意味です。",
    },
    features: {
      heading: "本気で上達したいコーチのために",
      subhead: "正直なフィードバックを、正しく構造化して、正直に答えられるプライバシー保証とともに。",
      cards: [
        { title: "本当に匿名", body: "アカウントなし、回答のIPフィンガープリントなし、コメントを保護者に結び付ける手段なし。人が本音を言うのは、そう確信できたときだけです。" },
        { title: "チームごとにオプトイン", body: "既定はオフ。コーチは準備ができたときにフィードバックを募集でき、チーム設定からいつでも一時停止できます。" },
        { title: "多次元レーダー", body: "笑顔・公平さ・スキル向上・応援・チーム熱量の5カテゴリー — ぼんやりした星ではなく、具体的なフィードバック。" },
        { title: "シーズン推移グラフ", body: "各次元の年間の動きを追跡。3月のスランプを、5月の退任になる前に見つけられます。" },
        { title: "自由記述コメント", body: "各回答に任意で書けるメモ。人が声に出しては言わない本音 — ここに本当の宝があります。" },
        { title: "選手間投票と補完", body: "トップダウンのフィードバック（保護者→コーチ）とボトムアップの承認（選手→選手）を同じプラットフォームで。2つの視点、1つの真実。" },
      ],
    },
    science: {
      eyebrow: "スポーツ心理学の裏付けあり",
      heading: "5つの観点の背景にある研究。",
      body: "2025年の介入研究で、Berntzen と Lagestad は14〜18歳の若手フットボーラー95人に、同じコーチのもとで同じ内容の練習を2回、1回はフィードバックあり、1回はなしで行わせました。フィードバックがあると、選手の習得感、コーチに見てもらえているという感覚、そしてモチベーションが高まりました。（Frontiers in Sports and Active Living, DOI 10.3389/fspor.2025.1527543）",
      link: "背後にある研究を読む",
    },
    comparison: {
      heading: "WhatsAppグループで聞けばよくない？",
      subhead: "メッセージに名前が載っていると、誰も正直に答えないからです。",
      columnFeature: "機能",
      columnUs: "Vote.GameOn",
      columnThem: "グループチャット／アンケート",
      rows: [
        "本当に匿名の回答",
        "1つの星ではなく5つの明確な次元を計測",
        "シーズンに1回ではなく毎試合後に取得可能",
        "保護者が普段いる場所（投票リンク）に存在",
        "推移グラフで時間経過の変化が見える",
        "コーチは無料",
      ],
      footnote: "典型的な草の根の代替手段（グループチャット投票と年1回のクラブアンケート）を2025年時点で比較。",
    },
    finalCta: {
      heading: "フィードバック収集を ",
      headlineHighlight: "今週から。",
      subhead: "コーチは無料。既定はオフ — 本音を聞く準備ができたときにオンに。",
      cta: "無料でチームを作る",
      footnote: "匿名。構造化。本当に役に立つ。",
    },
  },
};
